import { t } from 'i18next';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { FaceId, FaceNode } from '../../../../../../elements/design/design-node/utils/face-node';
import {
  findLineSegmentIdFromIntersection,
  getLineSegmentFaces,
} from '../../../../../../elements/design/design-node/utils/line-segment-node';
import { PointId } from '../../../../../../elements/design/design-node/utils/point-node';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';

export const flipBreakline = (tool: DesignTool, faces: FaceId[]): ContextMenuItem => {
  const lineSegmentId = findLineSegmentIdFromIntersection(tool.asset.designNode, tool.intersection);
  const disabled = !isDefined(lineSegmentId);

  return {
    title: t('contextMenu.flipBreakLine', { ns: 'skyviewDesign' }),
    action: () => {
      if (disabled) {
        return;
      }

      let changes: RevertChange[] = [];
      addAndCommitAction(tool, {
        commit: () => {
          changes = [];
          const getFaceValue = (face: FaceNode, index: number) => {
            return face.values[index % 3];
          };
          const line = tool.asset.designNode.getLineSegmentById(lineSegmentId);
          const faces = getLineSegmentFaces(tool.asset.designNode, lineSegmentId);

          if (faces.length !== 2) {
            throw new Error('Error flipping break line. Line is not connected exactly 2 faces.');
          }

          const faceA = tool.asset.designNode.getFaceById(faces[0]);
          const faceB = tool.asset.designNode.getFaceById(faces[1]);

          const aKeepIndex = faceA.values.findIndex((x) => !line.values.includes(x));
          const bKeepIndex = faceB.values.findIndex((x) => !line.values.includes(x));

          const aKeepValue = getFaceValue(faceA, aKeepIndex);
          const bKeepValue = getFaceValue(faceB, bKeepIndex);
          const sharedValues = [
            getFaceValue(faceA, aKeepIndex + 1),
            getFaceValue(faceA, aKeepIndex + 2),
          ];
          const aNewValues = faceA.values.map((x) => (x === sharedValues[0] ? bKeepValue : x)) as [
            PointId,
            PointId,
            PointId,
          ];
          const bNewValues = faceB.values.map((x) => (x === sharedValues[1] ? aKeepValue : x)) as [
            PointId,
            PointId,
            PointId,
          ];

          changes.unshift(tool.asset.designNode.upsertFace({ id: faceA.id, values: aNewValues }));
          changes.unshift(tool.asset.designNode.upsertFace({ id: faceB.id, values: bNewValues }));
          changes.unshift(
            tool.asset.designNode.upsertLineSegment({
              id: line.id,
              values: [aKeepValue, bKeepValue],
            }),
          );
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });
      tool.cursorLine.visible = false;
      tool.contextMenuCallback?.();
    },
    disabled,
  };
};
